import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { setNavigateFunction } from '../lib/axios';
import { useSession, useLogin, useLogout, authKeys } from '../api/auth';
import type { User } from '../api/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Disable session fetching on public auth pages where user shouldn't be logged in
  const isPublicAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/verify-email/') ||
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.startsWith('/token/');

  // Use React Query hooks for auth
  const { data: user, isLoading, refetch } = useSession({ enabled: !isPublicAuthPage });
  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  // Wrapper to maintain backward compatibility with existing login interface
  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await loginMutation.mutateAsync({ username, password });
      return { success: true };
    } catch (error) {
      // Extract error message for UI display
      // Note: Logging is handled by global mutationCache.onError in queryClient.ts
      const errorMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message || error.message
          : 'Login failed';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  // Register navigate function for global 401 handling
  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  const value: AuthContextType = {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading: isPublicAuthPage ? false : isLoading,
    login,
    logout,
    checkSession: async () => {
      await refetch();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Re-export for convenience
export { authKeys };
export type { User };
